import {
  Box,
  Button,
  Card,
  CardContent,
  Modal,
  Stack,
  Typography,
} from '@mui/material'
import { TFunction } from 'i18next'
import { FC, useCallback, useState } from 'react'
import { Fade, Icons, ItemImageArea } from 'utoniq-ui'

import {
  StepMissionRewardType,
  StepMissionType,
  UserStepMissionBoxFragment,
} from '@/clients/utoniq-core/schema'
import { CollectionIndicator, MissionLabel } from '@/components/organisms'
import { styleSets } from '@/components/styles'
import { config } from '@/config'
import { StorageImage, useMessage, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { Organism } from '@/core/architecture'

export type StepMissionBoxProps = {
  mission: UserStepMissionBoxFragment
  onGetStepMissionReward: (stepMissionId: string) => Promise<void>
}

export const StepMissionBox: Organism<StepMissionBoxProps> = ({
  mission,
  onGetStepMissionReward,
}) => {
  const { t } = useTranslation('artistMissions')
  const { showError } = useMessage()
  const { analytics } = useAnalytics()
  const [loading, setLoading] = useState<boolean>(false)
  const [openModal, setOpenModal] = useState<boolean>(false)
  const count = mission?.progress ?? 0
  const totalCount = mission?.step?.requiredCount ?? 0

  const isAcquirable = mission.isAccomplished && !mission.isRewardReceived

  const handleOnGet = useCallback(async () => {
    setLoading(true)
    await onGetStepMissionReward(mission?.id ?? '')
      .then(() => {
        setOpenModal(true)
        analytics &&
          analytics.track('receive_reward', {
            id: mission?.id,
            artistId: mission?.artistId,
            type: mission?.type,
          })
      })
      .catch(showError)
      .finally(() => setLoading(false))
  }, [onGetStepMissionReward, showError, mission?.id])

  return (
    <Card
      elevation={0}
      sx={{
        mt: 3,
        position: 'relative',
        overflow: 'visible',
        borderRadius: 2,
        bgcolor: 'grey.50',
      }}
    >
      <MissionLabel
        label={mission.label ?? null}
        sx={{
          position: 'absolute',
          top: -10,
          left: 20,
          zIndex: 1,
          width: 72,
          height: 20,
        }}
      />
      <CardContent
        sx={{ width: '100%', px: 5, pb: 4, '&:last-child': { pb: 4 } }}
      >
        <Stack
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: '16px 12px',
          }}
        >
          <Stack>
            <Typography
              sx={{
                width: '100%',
                wordBreak: 'break-all',
                ...styleSets.lineCamp(2),
              }}
              variant="h5"
            >
              {getStepMissionTitleByType(mission, t)}
            </Typography>
          </Stack>
          <StepMissionRewardContent stepMission={mission} />
          <Stack flex={1}>
            <CollectionIndicator
              itemCount={count}
              totalCount={totalCount}
              sx={{ mx: 0, py: 2 }}
              successColor="secondary"
            />
          </Stack>
          <Stack>
            <Button
              variant={isAcquirable ? 'outlined' : 'contained'}
              disabled={!isAcquirable}
              onClick={handleOnGet}
              loading={loading}
            >
              {t(mission.isRewardReceived ? 'earned' : 'get')}
            </Button>
          </Stack>
        </Stack>
        <GetStepMissionRewardModal
          open={openModal}
          onClose={() => setOpenModal(false)}
          stepMission={mission}
        />
      </CardContent>
    </Card>
  )
}

type GetStepMissionRewardModalProps = {
  open: boolean
  onClose: () => void
  stepMission: UserStepMissionBoxFragment
}
const GetStepMissionRewardModal: FC<GetStepMissionRewardModalProps> = ({
  open,
  onClose,
  stepMission,
}) => {
  const { t } = useTranslation('artistMissions')

  const RewardContent: FC<{
    stepMission: UserStepMissionBoxFragment
  }> = ({ stepMission }) => {
    switch (stepMission?.step?.reward?.type) {
      case StepMissionRewardType.PrepaidPoint:
        return <Icons.Point sx={{ fontSize: '80px' }} />
      case StepMissionRewardType.GachaTicket:
        return (
          <Box
            component="img"
            style={{ width: 80, height: 80 }}
            src={
              stepMission?.step?.reward?.metadata?.gachaTicket?.thumbnails
                ?.reward?.smallUri ?? ''
            }
          />
        )
      case StepMissionRewardType.Item:
        return (
          <ItemImageArea
            size="small"
            src={
              stepMission?.step?.reward?.metadata?.item?.thumbnail?.front
                ?.smallUri ?? ''
            }
          />
        )
      case StepMissionRewardType.ArtistPoint:
        return null
      default:
        return null
    }
  }
  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          width: 264,
          borderRadius: 3,
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'grey.100',
          outline: 'none',
          textAlign: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 6,
            pt: 6,
            backgroundImage: `url(${config.commonStorageUrl}images/bonus/loginbonus_bg_anime.png)`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
          }}
        >
          <Box sx={{ position: 'relative', height: 'fit-content' }}>
            <Box sx={{ display: 'grid', placeItems: 'center' }}>
              <StorageImage
                width="100%"
                target="common"
                src="images/bonus/loginbonus_title_bg.svg"
              />
            </Box>
            <Fade>
              <Box
                sx={{
                  position: 'absolute',
                  top: 4,
                  width: '100%',
                  height: '100%',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <Typography
                  textAlign="center"
                  variant="h5"
                  sx={{ height: '100%', color: 'white', pt: '2px' }}
                >
                  {t('missionAccomplishedTitle')}
                </Typography>
              </Box>
            </Fade>
          </Box>
          <Stack alignItems="center">
            <RewardContent stepMission={stepMission} />
            <Box
              sx={{
                bgcolor: 'grey.100',
                borderRadius: 100,
                minWidth: 64,
                mt: 2,
              }}
            >
              <Typography variant="subtitle2" textAlign="center">
                ×{stepMission?.step?.reward?.value ?? 0}
              </Typography>
            </Box>
            <Typography variant="h6" color="white" sx={{ pt: 3, pb: 4 }}>
              {t(
                `stepMissionAccomplishedDesc.${stepMission?.step?.reward?.type}`
              )}
            </Typography>
          </Stack>
        </Box>
        <Stack alignItems="center" sx={{ py: 5 }}>
          <Button onClick={onClose}>{t('close')}</Button>
        </Stack>
      </Box>
    </Modal>
  )
}

const getStepMissionTitleByType = (
  stepMission: UserStepMissionBoxFragment,
  t: TFunction
) => {
  switch (stepMission?.type) {
    case StepMissionType.SumCardsets:
      return t(`stepMissionTitle.${StepMissionType.SumCardsets}`, {
        requiredCount: stepMission?.step?.requiredCount,
      })
    case StepMissionType.SumRarities:
      return t(`stepMissionTitle.${StepMissionType.SumRarities}`, {
        requiredCount: stepMission?.step?.requiredCount,
      })
    case StepMissionType.SumComments:
      return t(`stepMissionTitle.${StepMissionType.SumComments}`, {
        requiredCount: stepMission?.step?.requiredCount,
      })
    case StepMissionType.SumTrades:
      return t(`stepMissionTitle.${StepMissionType.SumTrades}`, {
        requiredCount: stepMission?.step?.requiredCount,
      })
    case StepMissionType.CompletesCollections:
      return t(`stepMissionTitle.${StepMissionType.CompletesCollections}`, {
        requiredCount: stepMission?.step?.requiredCount,
      })
    case StepMissionType.ContinuousMembership:
      return t(`stepMissionTitle.${StepMissionType.ContinuousMembership}`, {
        requiredCount: stepMission?.step?.requiredCount,
      })
    default:
      return null
  }
}

const StepMissionRewardContent = ({
  stepMission,
}: {
  stepMission: UserStepMissionBoxFragment
}) => {
  switch (stepMission?.step?.reward?.type) {
    case StepMissionRewardType.PrepaidPoint:
      return (
        <Stack justifyContent="center" alignItems="center">
          <Icons.Point sx={{ fontSize: '48px' }} />
          <Typography
            align="center"
            variant="subtitle2"
            sx={{
              width: 56,
              borderRadius: '100px',
              bgcolor: 'grey.100',
              mt: -2,
            }}
          >
            ×{stepMission?.step?.reward?.value ?? 0}
          </Typography>
        </Stack>
      )
    case StepMissionRewardType.GachaTicket:
      return (
        <Stack justifyContent="center" alignItems="center">
          <Box
            component="img"
            style={{ width: 48, height: 48 }}
            src={
              stepMission?.step?.reward?.metadata?.gachaTicket?.thumbnails
                ?.reward?.smallUri ?? ''
            }
          />
          <Typography
            align="center"
            variant="subtitle2"
            sx={{
              width: 56,
              borderRadius: '100px',
              bgcolor: 'grey.100',
              mt: -2,
            }}
          >
            ×{stepMission?.step?.reward?.value ?? 0}
          </Typography>
        </Stack>
      )
    case StepMissionRewardType.Item:
      return (
        <Stack justifyContent="center" alignItems="center">
          <ItemImageArea
            size="x-small"
            src={
              stepMission?.step?.reward?.metadata?.item?.thumbnail?.front
                ?.smallUri ?? ''
            }
          />
          <Typography
            align="center"
            variant="subtitle2"
            sx={{
              width: 56,
              borderRadius: '100px',
              bgcolor: 'grey.100',
              mt: -2,
              zIndex: 1,
            }}
          >
            ×{stepMission?.step?.reward?.value ?? 0}
          </Typography>
        </Stack>
      )
    case StepMissionRewardType.ArtistPoint:
      return null
    default:
      return null
  }
}
